import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { not, eq } from 'ember-truth-helpers';
import set from 'ember-set-helper/helpers/set';
import {
  HdsFormCheckboxGroup,
  HdsButton,
  HdsLinkInline,
} from '@hashicorp/design-system-components/components';
import { Typography, Logo } from 'core/components';
import { boxMargin, boxPadding } from 'core/modifiers';
import { MarketingEmailStatuses } from 'core/utils/marketing-email';
import config from 'hcp/config/environment';

/**
 *
 * `ServiceAgreements` renders the HashiCorp cross-site service agreements form.
 * Once the form is completed, on submit, it redirects to Auth0.
 *
 *
 * ```
 * <Page::ServiceAgreements
 *   @marketingEmailStatus={{this.model.marketingEmailStatus}}
 * />
 * ```
 *
 * @class ServiceAgreementsComponent
 *
 */
export default class ServiceAgreements extends Component {
  @tracked termsOfService = false;
  @tracked privacyPolicy = false;
  @tracked _emailOptIn = false;
  @tracked showRequired = false;

  get hasCompletedRequiredChecks() {
    return this.termsOfService && this.privacyPolicy;
  }

  get formRedirectURL() {
    let params = new URL(window.location).searchParams;
    let state = params.get('state');

    return `https://${config.APP.auth0.domain}/continue?state=${state}&terms_of_service=${this.termsOfService}&privacy_policy=${this.privacyPolicy}&newsletter=${this.emailOptIn}`;
  }

  get emailOptIn() {
    if (this.args.marketingEmailStatus === MarketingEmailStatuses.MANDATORY) {
      return true;
    }

    return this._emailOptIn;
  }

  set emailOptIn(value) {
    if (this.args.marketingEmailStatus === MarketingEmailStatuses.MANDATORY) {
      this._emailOptIn = true;
    }

    this._emailOptIn = value;
  }

  onFormSubmission = (event) => {
    // we'll never submit the form since we're redirecting
    event.preventDefault();
    if (!this.hasCompletedRequiredChecks) {
      this.showRequired = true;
      return;
    }

    this.showRequired = false;

    return window.location.replace(this.formRedirectURL);
  };

  <template>
    <main id="content" class="service-agreements">
      <div class="service-agreements__gridContainer">
        <Logo />

        <form
          class="service-agreements__form"
          {{on "submit" this.onFormSubmission}}
        >
          <section aria-labelledby="requiredForm">
            <Typography @variant="h1" id="requiredForm">
              {{t "components.page.service-agreements.form.title"}}
            </Typography>

            <HdsFormCheckboxGroup
              @isRequired={{true}}
              {{boxPadding "0 0 md 0"}}
              as |G|
            >
              <G.Legend>
                <HdsLinkInline
                  @href={{t
                    "components.page.service-agreements.form.user-agreement-checkbox.external-link.url"
                  }}
                  @icon="external-link"
                  @iconPosition="trailing"
                  @isHrefExternal={{true}}
                >
                  {{t
                    "components.page.service-agreements.form.user-agreement-checkbox.external-link.text"
                  }}
                </HdsLinkInline>
              </G.Legend>

              <G.CheckboxField
                name="terms_of_service"
                data-test-termsOfService-checkbox
                {{on
                  "input"
                  (set this "termsOfService" (not this.termsOfService))
                }}
                as |F|
              >
                <F.Label>
                  {{t
                    "components.page.service-agreements.form.user-agreement-checkbox.label"
                  }}
                </F.Label>
              </G.CheckboxField>
            </HdsFormCheckboxGroup>

            <HdsFormCheckboxGroup
              @isRequired={{true}}
              {{boxPadding "0 0 md 0"}}
              as |G|
            >
              <G.Legend>
                <HdsLinkInline
                  @href={{t
                    "components.page.service-agreements.form.privacy-policy-checkbox.external-link.url"
                  }}
                  @icon="external-link"
                  @iconPosition="trailing"
                  @isHrefExternal={{true}}
                >
                  {{t
                    "components.page.service-agreements.form.privacy-policy-checkbox.external-link.text"
                  }}
                </HdsLinkInline>
              </G.Legend>

              <G.CheckboxField
                name="privacy_policy"
                data-test-privacyPolicy-checkbox
                {{on
                  "input"
                  (set this "privacyPolicy" (not this.privacyPolicy))
                }}
                as |F|
              >
                <F.Label>
                  {{t
                    "components.page.service-agreements.form.privacy-policy-checkbox.label"
                  }}
                </F.Label>
              </G.CheckboxField>
            </HdsFormCheckboxGroup>
          </section>

          {{#if (eq @marketingEmailStatus "mandatory")}}
            <section data-test-mandatory-marketing-email>
              <Typography @variant="h2" id="marketing-emails-mandatory">
                {{t
                  "components.page.service-agreements.form.email-preferences.title"
                }}
              </Typography>

              <label>
                {{t
                  "components.page.service-agreements.form.email-preferences.mandatory"
                }}
                <input
                  type="checkbox"
                  hidden="hidden"
                  name="newsletter"
                  checked={{this.emailOptIn}}
                />
              </label>
            </section>
          {{else}}
            <section
              data-test-optional-marketing-email
              aria-labelledby="marketing-emails-optional"
            >
              <HdsFormCheckboxGroup {{boxPadding "0 0 md 0"}} as |G|>
                <G.Legend>
                  <Typography @variant="h2" id="marketing-emails-optional">
                    {{t
                      "components.page.service-agreements.form.email-preferences.title"
                    }}
                  </Typography>
                </G.Legend>

                <G.CheckboxField
                  name="newsletter"
                  data-test-emailOptIn-checkbox
                  {{on "input" (set this "emailOptIn" (not this.emailOptIn))}}
                  as |F|
                >
                  <F.Label class="hds-font-weight-regular">
                    {{t
                      "components.page.service-agreements.form.email-preferences.opt-in-checkbox.label"
                    }}
                  </F.Label>
                </G.CheckboxField>
              </HdsFormCheckboxGroup>
            </section>
          {{/if}}

          <HdsButton
            data-test-tos-submit
            @text={{t
              "components.page.service-agreements.form.submit-button.text"
            }}
            type="submit"
            {{boxMargin "xl 0 0 0"}}
            disabled={{not this.hasCompletedRequiredChecks}}
          />
        </form>
      </div>
    </main>
  </template>
}
